import { useCallback, useEffect, useState } from 'react';
import {
  getGorgiasAuthUrl,
  getGorgiasConfig,
  getGorgiasErrorMessage,
  removeGorgiasAuth,
  saveGorgiasSubdomain
} from '../../services/integrations/gorgias.service';
import { ONBOARDING_OAUTH_KEY } from '../../interfaces/onboarding.types';
import useIsMountedRef from '../useIsMountedRef';

const useGorgiasSettings = () => {
  const isMounted = useIsMountedRef();
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [subdomainDraft, setSubdomainDraft] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [removeAuthLoading, setRemoveAuthLoading] = useState(false);
  const [removeAuthError, setRemoveAuthError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const config = await getGorgiasConfig();
      if (!isMounted.current) {
        return;
      }
      setSubdomain(config?.subdomain || null);
      setSubdomainDraft(config?.subdomain || '');
      setIsAuthenticated(config?.is_authenticated === true);
    } catch (error) {
      if (isMounted.current) {
        setLoadError(getGorgiasErrorMessage(error, 'Unable to load your Gorgias connection.'));
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveSubdomain = async (): Promise<boolean> => {
    if (!subdomainDraft.trim() || saveLoading || removeAuthLoading) {
      return false;
    }
    setSaveLoading(true);
    setSaveError(null);
    setAuthError(null);
    setRemoveAuthError(null);
    try {
      const normalizedSubdomain = await saveGorgiasSubdomain(subdomainDraft.trim());
      if (!isMounted.current) {
        return false;
      }
      if (normalizedSubdomain !== subdomain) {
        setIsAuthenticated(false);
      }
      setSubdomain(normalizedSubdomain);
      setSubdomainDraft(normalizedSubdomain);
      return true;
    } catch (error) {
      if (isMounted.current) {
        setSaveError(getGorgiasErrorMessage(error, 'Unable to save your Gorgias subdomain.'));
      }
      return false;
    } finally {
      if (isMounted.current) {
        setSaveLoading(false);
      }
    }
  };

  const startGorgiasOAuth = async (onboarding = false, openInNewWindow = false) => {
    if (!subdomain || authLoading || removeAuthLoading) {
      return;
    }
    // The optional onboarding catalog opens its existing panel in a dialog.
    // Open synchronously so authorization does not replace that onboarding page.
    const authorizationWindow = openInNewWindow ? window.open('about:blank', '_blank') : null;
    if (openInNewWindow && !authorizationWindow) {
      setAuthError('Allow pop-ups for Vennie, then try authenticating again.');
      return;
    }
    if (authorizationWindow) authorizationWindow.opener = null;
    setAuthLoading(true);
    setAuthError(null);
    setRemoveAuthError(null);
    try {
      const url = await getGorgiasAuthUrl();
      if (!isMounted.current) {
        authorizationWindow?.close();
        return;
      }
      if (onboarding) {
        localStorage.setItem(ONBOARDING_OAUTH_KEY, 'gorgias');
      } else {
        localStorage.removeItem(ONBOARDING_OAUTH_KEY);
      }
      if (authorizationWindow) {
        // Ensure the callback emits a storage event even when reconnecting.
        if (localStorage.getItem('connected') === 'gorgias') localStorage.removeItem('connected');
        authorizationWindow.location.href = url;
        setAuthLoading(false);
      } else {
        window.location.href = url;
      }
    } catch (error) {
      authorizationWindow?.close();
      if (isMounted.current) {
        setAuthError(getGorgiasErrorMessage(error, 'Unable to start Gorgias authentication.'));
        setAuthLoading(false);
      }
    }
  };

  const removeGorgiasAuthentication = async () => {
    if (removeAuthLoading || saveLoading || authLoading) {
      return;
    }
    setRemoveAuthLoading(true);
    setRemoveAuthError(null);
    try {
      await removeGorgiasAuth();
      if (!isMounted.current) {
        return;
      }
      setIsAuthenticated(false);
      setAuthError(null);
      if (localStorage.getItem('connected') === 'gorgias') {
        localStorage.removeItem('connected');
      }
    } catch (error) {
      if (isMounted.current) {
        setRemoveAuthError(getGorgiasErrorMessage(error, 'Unable to remove Gorgias authentication. Please try again.'));
      }
    } finally {
      if (isMounted.current) {
        setRemoveAuthLoading(false);
      }
    }
  };

  return {
    subdomain,
    subdomainDraft,
    setSubdomainDraft,
    isAuthenticated,
    loading,
    loadError,
    loadConfig,
    saveLoading,
    saveError,
    saveSubdomain,
    authLoading,
    authError,
    startGorgiasOAuth,
    removeAuthLoading,
    removeAuthError,
    removeGorgiasAuthentication
  };
};

export default useGorgiasSettings;
